import getIsElementOutsideViewport from '../../documentUtilities/getIsElementOutsideViewport';

/**
 * This method takes a list of objects that represent words (by pointing to elements in the content)
 * and reflects the fact that they are currently being read by
 * a) adding a highlight class to each of their parent elements
 * b) scrolling the element into view if it is not already visible
 *
 * The difference between passing a list of word objects and a single word object depends on whether that level of context is available.
 * In Chrome for Android, the onboundary event never fires, so we can not know the specific word being read.
 * However, we always have access to the Utterance, which is roughly a line of text.
 * We will know that the word being read is at least within the words contained by the Utterance.
 *
 * @method reflectCurrentTTSWordObjects
 * @param {Element} frame - the frame element
 * @param {Array<Object>} wordObjects - objects representing TTS context for words in the content
 */
const reflectCurrentTTSWordObjects = (frame, wordObjects = []) => {
  const frameDocument = frame.contentDocument;

  // Add highlighting to indicate the current section of content being read.
  wordObjects
    .map((wordObject) => wordObject.parentElements)
    .flat()
    .forEach((parentElement) => parentElement.classList.add('is-tts-active'));

  const activeTTSContainer =
    frameDocument.getElementsByClassName('is-tts-active')[0];

  const rangeRectangle = wordObjects[0]?.rangeRectangle;
  const scrollContainer = frame.parentElement.parentElement;

  // Check if rangeRectangle is valid (has non-zero dimensions)
  // For non-text elements like <img>, rangeRectangle may be empty (all zeros)
  const isRangeRectangleValid =
    rangeRectangle && (rangeRectangle.width > 0 || rangeRectangle.height > 0);

  // Use rangeRectangle if valid, otherwise fall back to element's bounding rect
  const elementRect = isRangeRectangleValid
    ? rangeRectangle
    : activeTTSContainer?.getBoundingClientRect();

  if (
    activeTTSContainer &&
    elementRect &&
    getIsElementOutsideViewport(elementRect, frame)
  ) {
    // There is a bug with using scrollIntoView where it will scroll ALL parents
    // which ends up scrolling the entire Reader and not just the Document

    // elementRect.top is the position relative to the iframe document (not browser viewport)
    // This is essentially the absolute position within the document
    // We just need to scroll to that position minus a small offset
    scrollContainer.scrollTop = elementRect.top - 10;
  }
};

export default reflectCurrentTTSWordObjects;
